import dayjs from 'dayjs';
import { useContext } from 'react';
import { AppContext } from '../../../../App';
import { getTenant } from '../../../../Utils/storageUtils';
import { TenantType } from '../../../../Utils/types';
import { formatTransactionAmount } from '../../../../Utils/utils';
import { getInitials, shouldShowCategoryInitials } from '../../../../Utils/merchantLogoUtils';
import { getCategoryDisplayName, getCategoryIcon, CATEGORY_ICONS } from '../../../JFS/Common/CategoryMapping';
import styles from './index.module.css';

const TransactionCard: React.FC<{
  transaction: any;
  iconSize?: any;
  transactionImageBGProp?: any;
  showDateOfTransaction?: boolean;
  isPendingRec?: boolean;
  isCompletedRec?: boolean;
}> = ({
  transaction,
  iconSize,
  transactionImageBGProp,
  showDateOfTransaction,
  isCompletedRec,
  isPendingRec,
}) => {
  const { isAmountVisible }: any = useContext(AppContext);
  const merchantLogoCode =  transaction.merchantLogoCode;
  
  const categoryDisplayName = getCategoryDisplayName(transaction.categoryCode || transaction.category || '');
  const hasCategoryIcon = categoryDisplayName !== 'Others' && CATEGORY_ICONS[categoryDisplayName];
  const categoryIcon = hasCategoryIcon ? getCategoryIcon(categoryDisplayName) : null;

  let merchantName;
  if (transaction && transaction.displayName) {
    merchantName = transaction.displayName.split('-');
  } else if (transaction && transaction.category) {
    merchantName = transaction.category.split('-');
  } else if (transaction && transaction.narration) {
    merchantName = transaction.narration.split('-');
  }

  return (
    <>
      <div className={styles.transactionLeft}>
        <div className={styles.avatarContainer}>
          <div 
            className={styles.transactionAvatar}
            style={{ 
              backgroundColor: (getTenant() === TenantType.JFS && !categoryIcon && shouldShowCategoryInitials(transaction.merchantName, merchantLogoCode))
                ? (transaction.backgroundColor)
                : 'transparent'
            }}
          >
            {(getTenant() === TenantType.JFS && categoryIcon) ? (
              <img 
                src={categoryIcon}
                alt={categoryDisplayName}
                className={styles.merchantLogo}
              />
            ) : (getTenant() === TenantType.JFS && shouldShowCategoryInitials(transaction.merchantName, merchantLogoCode)) ? (
              <span 
                className={styles.avatarText}
                style={{ 
                  fontSize: '14px',
                  fontWeight: '500',
                  color: transaction && transaction.type && transaction.type.toLowerCase().startsWith('c') 
                    ? '#00A852' 
                    : '#CE912D'
                }}
              >
                {getInitials(transaction.category)}
              </span>
            ) : (transaction.merchantLogo || transaction.avatar) ? (
              <img 
                src={transaction.merchantLogo || transaction.avatar}
                alt={transaction.displayName || transaction.counterparty || transaction.category || 'Merchant'}
                className={styles.merchantLogo}
                onError={(e) => {
                  // Fallback to category icon if merchant logo fails to load
                  const target = e.target as HTMLImageElement;
                  // Prevent infinite error loops
                  if (target.dataset.fallbackAttempted) {
                    return;
                  }
                  target.dataset.fallbackAttempted = 'true';
                  
                  if (transaction.fallbackLogo && target.src !== transaction.fallbackLogo) {
                    target.src = transaction.fallbackLogo;
                    target.style.display = 'block';
                  } else {
                    // If no fallback logo or fallback also failed, fall back to initials
                    target.style.display = 'none';
                    const avatarElement = target.parentElement;
                    if (avatarElement && !avatarElement.querySelector(`.${styles.avatarText}`)) {
                      const fallbackSpan = document.createElement('span');
                      fallbackSpan.className = styles.avatarText;
                      fallbackSpan.style.color = transaction && transaction.type && transaction.type.toLowerCase().startsWith('c') 
                        ? '#00A852' 
                        : '#CE912D';
                      fallbackSpan.textContent =  getInitials(transaction.category || 'UN');
                      avatarElement.appendChild(fallbackSpan);
                    }
                  }
                }}
              />
            ) : (
              <span 
                className={styles.avatarText}
                style={{ 
                  color: transaction && transaction.type && transaction.type.toLowerCase().startsWith('c') 
                    ? '#00A852' 
                    : '#CE912D'
                }}
              >
                {getInitials( transaction.category || 'UN')}
              </span>
            )}
          </div>
        </div>
        
        <div className={styles.transactionInfo}>
          <div className={styles.transactionCategory}>
            {transaction.displayName || transaction.counterparty || transaction.category || 'Unknown'}
          </div>
          {transaction.id && (
            <div className={styles.transactionDescription}>
              {transaction.description || 'Transaction'}
            </div>
          )}
        </div>
      </div>

      <div className={styles.transactionRight}>
        <div className={styles.transactionAmountContainer}>
          <div 
            className={`${styles.transactionAmount} ${
              transaction && transaction.type && transaction.type.toLowerCase().startsWith('c')
                ? styles.creditAmount
                : styles.debitAmount
            }`}
          >
            {transaction && (transaction.amount || transaction.format)
              ? transaction.format
                ? `${formatTransactionAmount(
                    transaction.lastAmount, 
                    transaction.type || 'debit',
                    getTenant() === TenantType.PB ? true : isAmountVisible
                  )}`
                : `${formatTransactionAmount(
                    transaction.amount.toString(),
                    transaction.type || 'debit',
                    getTenant() === TenantType.PB ? true : isAmountVisible
                  )}`
              : ''}
          </div>

          {showDateOfTransaction && (
            <div className={styles.dateRecurringTxn}>
              {dayjs(
                transaction.dateOfTransaction
                  ? transaction.dateOfTransaction
                  : isCompletedRec
                  ? transaction.lastTransactionDate
                  : isPendingRec
                  ? transaction.upcomingDate
                  : transaction.dateOfTransaction
              ).format("on D MMM'YY")}
            </div>
          )}
        </div>
      </div>
    </>
  );

};

export default TransactionCard;
